import { forwardRef, useEffect, useMemo, useRef } from 'react';
import useMergedRef from '@react-hook/merged-ref';
import { Layout } from '../../../types';
import { useBanner } from '../../bannerProvider';
import type { MediaBlockProps } from '../components';
import { MediaBlock } from '../components';
import type { BaseVideoBlockProps, PlayerAPI } from './BaseVideoBlock';
import { BaseVideoBlock } from './BaseVideoBlock';
import { SideVideoBlock } from './SideVideoBlock';
import { videoSettings } from './videoSettings';

export type VideoBlockProps = BaseVideoBlockProps & {
  alignment?: MediaBlockProps['alignment'];
  autoplay?: boolean;
  muted?: boolean;
  layout?: Layout;
  mobileLayout?: Layout;
  areaWidth?: number;
  videoHeight?: number;
  applyMobileSettings: boolean;
  mobileScale: number;
  mobileRatio: number;
  horizontalSize?: number;
  horizontalSizeOnMobile?: number;
  horizontalBorderRadius?: number;
  verticalBorderRadius?: number;
  isFormSubmitted: boolean;
};

export const VideoBlock = forwardRef<PlayerAPI, VideoBlockProps>(
  (
    {
      alignment,
      autoplay = videoSettings.autoplay,
      muted = videoSettings.muted,
      scale = videoSettings.scale,
      ratio = videoSettings.ratio,
      style,
      videoHeight,
      applyMobileSettings,
      layout = videoSettings.layout,
      mobileLayout = videoSettings.mobileLayout,
      mobileScale = videoSettings.mobileScale,
      mobileRatio = videoSettings.mobileRatio,
      horizontalSize = videoSettings.horizontalSize,
      horizontalSizeOnMobile = videoSettings.horizontalSizeOnMobile,
      horizontalBorderRadius,
      verticalBorderRadius,
      isFormSubmitted,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const { contentDirection, stretchBackground, mobileView, canPlayMedia } =
      useBanner();

    const playerRef = useRef<PlayerAPI>(null);
    const mergedRef = useMergedRef(forwardingRef, playerRef);

    const isColumn = contentDirection === 'column';

    const defaultScale = applyMobileSettings
      ? videoSettings.mobileScale
      : videoSettings.scale;

    const defaultRatio = applyMobileSettings
      ? videoSettings.mobileRatio
      : videoSettings.ratio;

    const currentLayout = applyMobileSettings ? mobileLayout : layout;

    const isSideMediaLayout = [Layout.LeftMedia, Layout.RightMedia].includes(
      currentLayout
    );
    const isSideBannerLayout = [Layout.LeftBanner, Layout.RightBanner].includes(
      currentLayout
    );
    const isEmbeddedMedia =
      currentLayout === Layout.EmbeddedMedia ||
      (mobileView && isSideMediaLayout);
    const isEmbeddedBanner =
      currentLayout === Layout.EmbeddedBanner ||
      (mobileView && isSideBannerLayout);

    const borderRadius = useMemo(() => {
      if (!isColumn) {
        return horizontalBorderRadius;
      }

      if (isEmbeddedBanner || isSideBannerLayout) {
        return;
      }

      return verticalBorderRadius;
    }, [
      isColumn,
      isEmbeddedBanner,
      isSideBannerLayout,
      horizontalBorderRadius,
      verticalBorderRadius
    ]);

    const calculatedScale = useMemo(() => {
      const value = applyMobileSettings ? mobileScale : scale;

      if (isColumn) {
        if (isEmbeddedMedia || isSideMediaLayout) {
          return value;
        }

        if (isEmbeddedBanner || isSideBannerLayout) {
          return 100;
        }
      }

      return defaultScale;
    }, [
      applyMobileSettings,
      mobileScale,
      scale,
      isColumn,
      isEmbeddedBanner,
      isEmbeddedMedia,
      isSideBannerLayout,
      isSideMediaLayout,
      defaultScale
    ]);

    const calculatedRatio = useMemo(() => {
      const value = applyMobileSettings ? mobileRatio : ratio;

      if (isColumn) {
        if (isEmbeddedBanner) {
          return stretchBackground ? 0 : value;
        }

        if (isSideBannerLayout) {
          return 0;
        }
      }

      return defaultRatio;
    }, [
      applyMobileSettings,
      mobileRatio,
      ratio,
      isColumn,
      stretchBackground,
      isSideBannerLayout,
      isEmbeddedBanner,
      isSideBannerLayout,
      defaultRatio
    ]);

    const calculatedSize = applyMobileSettings
      ? horizontalSizeOnMobile / calculatedRatio
      : horizontalSize / calculatedRatio;

    const commonProps = {
      ...(forwardingProps as VideoBlockProps),
      ref: mergedRef,
      borderRadius,
      scale: calculatedScale,
      ratio: calculatedRatio,
      style: isColumn ? style : { width: `${calculatedSize}px`, ...style }
    };

    const showSideBlock =
      isColumn &&
      !isFormSubmitted &&
      (isSideBannerLayout || isSideMediaLayout) &&
      !mobileView;

    useEffect(() => {
      const { current: player } = playerRef;

      if (!player) {
        return;
      }

      autoplay && canPlayMedia ? player.play() : player.pause();
      muted ? player.turnOffSound() : player.turnOnSound();
    }, [playerRef.current, autoplay, canPlayMedia, muted]);

    return (
      <>
        {showSideBlock ? (
          <SideVideoBlock {...commonProps} layout={currentLayout} />
        ) : (
          <MediaBlock
            alignment={alignment}
            layout={currentLayout}
            height={videoHeight}
            style={commonProps.style}
          >
            <BaseVideoBlock {...commonProps} />
          </MediaBlock>
        )}
      </>
    );
  }
);
